import { WEEKDAY_INITIALS } from '@practium/ui';
import { CROP_PHASE_LABELS } from '../../constants/maintenance.constants';
import {
	formatCycleDayRange,
	type CycleCalendarDay,
	type CycleCalendarMonth,
} from '../../utils/dynamicParcellingCycle';
import { CyclePhaseDot, cyclePhaseClass } from './CyclePhaseDot';
import styles from './CycleMonthCard.module.css';

const DAYS_PER_WEEK = 7;

const toWeeks = ({ offset, days }: CycleCalendarMonth) => {
	const cells: Array<CycleCalendarDay | null> = [...Array<null>(offset).fill(null), ...days];
	return Array.from({ length: Math.ceil(cells.length / DAYS_PER_WEEK) }, (_, index) =>
		cells.slice(index * DAYS_PER_WEEK, (index + 1) * DAYS_PER_WEEK)
	);
};

const coloredIndexes = (week: Array<CycleCalendarDay | null>) =>
	week.flatMap((cell, index) => (cell?.phase ? [index] : []));

const dayClassName = (
	day: CycleCalendarDay,
	weekIndex: number,
	week: Array<CycleCalendarDay | null>,
	days: CycleCalendarDay[]
) => {
	const colored = coloredIndexes(week);
	const index = day.day - 1;
	const startsPhase = !!day.phase && days[index - 1]?.phase !== day.phase;
	const endsPhase = !!day.phase && days[index + 1]?.phase !== day.phase;
	const isFirst = colored[0] === weekIndex || startsPhase;
	const isLast = colored[colored.length - 1] === weekIndex || endsPhase;
	return [
		styles.day,
		day.phase ? styles.dayFilled : '',
		cyclePhaseClass(day.phase),
		isFirst ? styles.dayFirst : '',
		isLast ? styles.dayLast : '',
	]
		.filter(Boolean)
		.join(' ');
};

export const CycleMonthCard = ({ month }: { month: CycleCalendarMonth }) => {
	const phaseCount = month.legend.length;

	return (
		<article className={styles.card}>
			<header className={styles.header}>
				<h4 className={styles.title}>
					{month.label} <span className={styles.year}>{month.year}</span>
				</h4>
				<span className={styles.phaseCount}>
					{phaseCount} {phaseCount === 1 ? 'fase' : 'fases'}
				</span>
			</header>
			<div className={styles.calendar}>
				<div className={styles.week}>
					{WEEKDAY_INITIALS.map((initial, index) => (
						<span key={`${initial}-${index}`} className={styles.weekday}>
							{initial}
						</span>
					))}
				</div>
				{toWeeks(month).map((week, weekIndex) => (
					<div key={`${month.key}-${weekIndex}`} className={styles.week}>
						{week.map((day, index) =>
							day ? (
								<span key={day.day} className={dayClassName(day, index, week, month.days)}>
									{day.day}
								</span>
							) : (
								<span key={`empty-${index}`} />
							)
						)}
					</div>
				))}
			</div>
			<ul className={styles.legend}>
				{month.legend.map((item) => (
					<li key={item.phase} className={styles.legendItem}>
						<CyclePhaseDot phase={item.phase} round />
						<span className={styles.legendPhase}>
							{CROP_PHASE_LABELS[item.phase] ?? item.phase}
						</span>
						<span className={styles.legendDays}>{formatCycleDayRange(item)}</span>
					</li>
				))}
			</ul>
		</article>
	);
};
